import ReCAPTCHA from "react-google-recaptcha"
import { Fragment, useEffect, useState, useRef } from "react"
import { useAppContext, usePropsObject } from "@/lib/contexts/appContext"
import { useMember } from "@/lib/hooks/useMember"
import Link from "next/link"
import { updateMemberSettings } from "@/lib/api/member"
import { getMemberToken } from "@/lib/helpers/member"
import Block from "@/components/wrappers/block"
import Done from "@/common/icons/member/done"
import Elevator from "@/common/icons/event/elevator"
import Ramp from "@/common/icons/event/ramp"
import Wheelchair from "@/common/icons/event/wheelchair"
import Toilet from "@/common/icons/event/toilet"
import ToiletAlt from "@/common/icons/event/toilet-alt"
import Microphone from "@/common/icons/event/microphone"
import HearingAid from "@/common/icons/event/hearing-aid"
import Button from "@/common/button/button"
import EventMap from "./event-map"
import classNames from "classnames"

export default function EventInfo({ block, settings }) {
  const object = usePropsObject()

  return (
    <Block width="bleed" gap="small" block={block} className="meta-wrapper">
      <div className="inner-wrapper">
        <Information {...{ object, settings }} />
        <Details {...{ object, settings }} />
        <Accessability {...{ object }} />
        <Contact {...{ object }} />
        <Location {...{ object }} />
      </div>
    </Block>
  )
}

export const Information = ({ object, settings, renderButton = true }) => {
  const appContext = useAppContext()
  const [hideButton, setHidden] = useState(true)

  const handleScroll = () => {
    if (!appContext.checkoutFormRef.current) {
      return
    }

    const formElem = appContext.checkoutFormRef.current.getBoundingClientRect()
    const formInView =
      appContext.checkoutFormRef.current.offsetTop <
      window.scrollY + formElem.height
    const hasScrolled = window.scrollY > 100

    setHidden(!hasScrolled || formInView || !settings?.active)
  }

  useEffect(() => {
    window.addEventListener("scroll", handleScroll, {
      passive: true
    })

    return () => {
      window.removeEventListener("scroll", handleScroll)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const scrollToForm = () => {
    const header = document.getElementById("header")
    const formTop = appContext.checkoutFormRef.current.offsetTop

    window.scrollTo({
      top: formTop - header.offsetHeight - 30,
      behavior: "smooth"
    })
  }

  const list = [
    {
      label: "Datum",
      value: [object.meta.dateString]
    },
    {
      label: "Tid",
      value: [object.meta.timeString]
    },
    {
      label: "Plats",
      value: [object.meta.location?.address, object.meta.location?.city]
    },
    {
      label:
        Array.from(object.meta.categories).length === 1
          ? "Kategori"
          : "Kategorier",
      value: object.meta.categories
    }
  ]

  const items = list.filter((item) => {
    item.value = item.value.filter(Boolean).join(", ").trim()
    return String(item.value).length > 0
  })

  if (items.length === 0) {
    return null
  }

  return (
    <section>
      <h2>Information</h2>
      <ul>
        {items.map((item, key) => {
          return (
            <li key={"info-" + key}>
              <span className="label">{item.label}:</span>
              <span className="text">{item.value}</span>
            </li>
          )
        })}
      </ul>
      {renderButton && settings?.active && (
        <div
          className={classNames([
            "button-wrapper",
            { "hide-button": hideButton }
          ])}>
          <Button
            ariaLabel="Anmäl dig"
            theme="green"
            onClick={() => scrollToForm()}>
            Anmäl dig
          </Button>
        </div>
      )}
      <FavoriteButton />
    </section>
  )
}

const Details = ({ object, settings }) => {
  const list = [
    {
      label: "Målgrupp",
      url: false,
      value: [object.meta.ageGroup]
    },
    {
      label: "Antal platser",
      url: false,
      value: [object.meta.signup.maxParticipants]
    },
    {
      label: "Antal lediga platser",
      url: false,
      value:
        settings.active && settings.max > 0 && settings.available > 0
          ? [settings.available]
          : []
    },
    {
      label: "Anmälan stänger",
      url: false,
      value: [object.meta.signup.openUntil]
    },
    {
      label: "Medlemskap krävs",
      url: false,
      value: [object.meta.signup.membership ? "Ja" : "Nej"]
    },
    {
      label: "Arrangör",
      url: true,
      value: object.meta.organizers.list
    },
    {
      label: "Medverkar",
      url: false,
      value: [object.meta?.people]
    },
    {
      label: "Pris",
      value: [getPrice(object)]
    }
  ]

  const items = list.filter((item) => {
    if (item.url) return true
    item.value = item.value.filter(Boolean).join(item?.join ?? ", ")
    return String(item.value).length > 0
  })

  return (
    <section>
      <ul>
        {items.map((item, key) => {
          return (
            <li key={item + key}>
              <span className="label">{item.label}:</span>
              <span className="text">
                {item.url &&
                  item.value.map((link, index) => {
                    return (
                      <Fragment key={link.label}>
                        <Link href={link.url} prefetch={false}>
                          {link.label}
                        </Link>
                        {index < item.value.length - 1 && ", "}
                      </Fragment>
                    )
                  })}
                {!item.url && item.value}
              </span>
            </li>
          )
        })}
      </ul>
    </section>
  )
}

const Location = ({ object }) => {
  const { location } = object.meta
  const address = [
    (location?.address ?? "").trim(),
    (location?.city ?? "").trim()
  ].filter(Boolean)

  const gathering = [
    location.gatheringAddress,
    location.gatheringPlace,
    location.gatheringDescription
  ].filter(Boolean)

  if (address.length === 0 && gathering.length === 0) {
    return null
  }

  return (
    <section className="location">
      <h2 className="">Hitta hit</h2>
      {address.length > 0 && (
        <ul>
          {address.map((row, i) => (
            <li key={`gathering-row-${i}`}>{row}</li>
          ))}
        </ul>
      )}
      {gathering.length > 0 && (
        <>
          <strong>Samlingsplats: </strong>
          {gathering.join(", ")}
        </>
      )}
      <EventMap object={object} />
    </section>
  )
}

const Contact = ({ object }) => {
  if (!object.meta.contact.name) {
    return null
  }

  return (
    <section>
      <h2>Kontaktperson</h2>
      <ul>
        <li>{object.meta.contact.name}</li>
        <li className="email">
          <a
            href={
              "mailto:" + object.meta.contact.email + "?subject=" + object.title
            }>
            {object.meta.contact.email}
          </a>
        </li>
        <li>
          <a href={"tel:" + object.meta.contact.phone}>
            {object.meta.contact.phone}
          </a>
        </li>
      </ul>
    </section>
  )
}

const Accessability = ({ object }) => {
  if (object.meta.accessability.length === 0) {
    return null
  }

  const Icon = ({ name }) => {
    const iconMap = {
      elevator: <Elevator className="w-7" />,
      ramp: <Ramp className="w-7" />,
      wheelchair: <Wheelchair className="w-7" />,
      toilet: <Toilet className="w-7" />,
      "toilet-alt": <ToiletAlt className="w-7" />,
      microphone: <Microphone className="w-7" />,
      "hearing-aid": <HearingAid className="w-7" />
    }

    return iconMap[name] ?? null
  }

  const items = object.meta.accessability.map((item, key) => {
    return (
      <li key={"access-" + key}>
        <div className="circle">
          <div className="icon">
            <Icon name={item.icon} />
          </div>
        </div>
        <span>{item.label}</span>
      </li>
    )
  })

  return (
    <section>
      <h2>Tillgänglighet</h2>
      <ul className="with-icons">{items}</ul>
    </section>
  )
}

const getPrice = (object) => {
  const { price, priceMember, priceType } = object.meta.signup

  if (price === 0) {
    return [priceType]
  }

  const priceArray = []

  if (price > 0) {
    priceArray.push(`${price} SEK`)
  }

  if (priceMember !== null && priceMember < price) {
    if (priceMember === 0) {
      priceArray.push(`(gratis för medlemmar)`)
    } else {
      priceArray.push(`(${priceMember} SEK för medlemmar)`)
    }
  }

  return priceArray.join(" ")
}

const FavoriteButton = () => {
  const { hasMounted, isLoggedIn, token, isLoading, menuItems, events } =
    useMember()
  const siteKey = process.env.NEXT_PUBLIC_CAPTCHA_SITE_KEY
  const recaptchaRef = useRef()
  const object = usePropsObject()
  const [isFavorite, setFavorite] = useState(false)
  const [inProgress, setInProgress] = useState(false)
  const [message, setMessage] = useState("")
  const member = getMemberToken()

  useEffect(() => {
    const favorites = events?.favorites ?? []
    setFavorite(favorites.includes(object.activityId))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events])

  if (!member) {
    return null
  }

  if (!hasMounted && !isLoggedIn && !isLoading) {
    return null
  }

  const buttonLabel = isLoading
    ? "Laddar"
    : isFavorite
    ? "Favoritmarkerad"
    : "Favoritmarkera"

  const eventItem = menuItems.find((item) => item.props.isEvents === true)

  const requestFavorite = async () => {
    setMessage("")
    const newEvents = { ...events }
    newEvents.favorites = Array.isArray(newEvents.favorites)
      ? newEvents.favorites
      : []

    if (isFavorite) {
      newEvents.favorites = [
        ...events.favorites.filter((event) => event !== object.activityId)
      ]
    } else {
      newEvents.favorites.push(object.activityId)
    }

    newEvents.favorites = [...new Set(newEvents.favorites)].sort(
      (a, b) => a - b
    )

    setInProgress(true)
    const captcha = await recaptchaRef.current.executeAsync()
    const response = await updateMemberSettings(token, captcha, {
      events: newEvents
    })

    if (response.status) {
      setInProgress(false)
      setFavorite(!isFavorite)
      const message = isFavorite ? "removed" : "added"

      setMessage(message)
    }
  }

  return (
    <div className="favorite-event">
      <ReCAPTCHA
        className="hidden"
        ref={recaptchaRef}
        size="invisible"
        sitekey={siteKey}
      />
      <Button
        theme="lime"
        disabled={isLoading || inProgress}
        className={classNames({ "is-favorite": isFavorite })}
        onClick={requestFavorite}
        ariaLabel={buttonLabel}
        loading={inProgress || isLoading}>
        <div className="circle">{isFavorite && <Done />}</div>
        {buttonLabel}
      </Button>
      {message === "removed" && <p>Borttagen från favoriter</p>}
      {message === "added" && eventItem.url && (
        <p>
          Evenemanget är tillagt i{" "}
          <Link
            href={eventItem.url + "?tab=member"}
            prefetch={false}
            legacyBehavior>
            <a>din kalender</a>
          </Link>
        </p>
      )}
    </div>
  )
}
